import {
  createContext,
  type PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { useReducedMotion } from "motion/react";
import { useUIStore } from "@stores/useUIStore";
import { CelebrationHost } from "./CelebrationHost";
import { shouldBypassCelebrationCooldown } from "./celebrationConfetti";
import type {
  ActiveCelebration,
  CelebrationOptions,
  ProductCelebration,
} from "./celebration.types";
import { useCelebrationEffects } from "./useCelebrationEffects";

type CelebrationContextValue = {
  celebrate: (options: CelebrationOptions) => void;
};

const CELEBRATION_COOLDOWN_MS = 300;
const CelebrationContext = createContext<CelebrationContextValue | null>(null);

export {
  getCelebrationDurationMs,
  shouldBypassCelebrationCooldown,
} from "./celebrationConfetti";

export function CelebrationProvider({ children }: PropsWithChildren) {
  const [activeCelebration, setActiveCelebration] =
    useState<ActiveCelebration | null>(null);
  const activeCelebrationRef = useRef<ActiveCelebration | null>(null);
  const idRef = useRef(0);
  const lastCelebrationAtRef = useRef(0);
  const celebrationMuted = useUIStore((s) => s.celebrationMuted);
  const animationReduced = useUIStore((s) => s.animationReduced);
  const systemReducedMotion = useReducedMotion();
  const shouldReduceMotion = Boolean(animationReduced || systemReducedMotion);
  const { canvasRef, runEffects } = useCelebrationEffects({
    muted: celebrationMuted,
    reducedMotion: shouldReduceMotion,
  });

  useEffect(
    () => () => {
      activeCelebrationRef.current = null;
    },
    [],
  );

  const dismiss = useCallback(() => {
    activeCelebrationRef.current = null;
    setActiveCelebration(null);
  }, []);

  const activate = useCallback(
    (options: ProductCelebration) => {
      const nextCelebration: ActiveCelebration = {
        ...options,
        asset: options.asset ?? "bbb-logo",
        id: idRef.current + 1,
      };
      idRef.current = nextCelebration.id;
      activeCelebrationRef.current = nextCelebration;
      setActiveCelebration(nextCelebration);
    },
    [],
  );

  const celebrate = useCallback(
    (options: CelebrationOptions) => {
      const now = Date.now();
      if (
        !shouldBypassCelebrationCooldown(options.type) &&
        now - lastCelebrationAtRef.current < CELEBRATION_COOLDOWN_MS
      ) {
        return;
      }
      lastCelebrationAtRef.current = now;

      if (options.type === "burst" || options.type === "fireworks") {
        runEffects(options);
        return;
      }

      const productOptions = options as ProductCelebration;
      if (
        productOptions.type === "block-generated" &&
        activeCelebrationRef.current?.type === "achievement-unlocked"
      ) {
        return;
      }

      activate(productOptions);
      runEffects(productOptions);
    },
    [activate, runEffects],
  );

  return (
    <CelebrationContext.Provider value={{ celebrate }}>
      {children}
      <CelebrationHost
        celebration={activeCelebration}
        onDismiss={dismiss}
        shouldReduceMotion={shouldReduceMotion || celebrationMuted}
      />
      <canvas
        ref={canvasRef}
        aria-hidden
        className="pointer-events-none fixed inset-0 z-[9999] size-full"
      />
    </CelebrationContext.Provider>
  );
}

export function useCelebration() {
  const context = useContext(CelebrationContext);
  if (!context) {
    throw new Error("useCelebration must be used inside CelebrationProvider");
  }
  return context;
}
